function isValidYmd(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function toIso(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseDmyParts(day: number, month: number, year: number): string {
  if (year < 100) year += year < 50 ? 2000 : 1900;
  if (!isValidYmd(year, month, day)) return '';
  return toIso(year, month, day);
}

const MONTH_NAME_TO_NUMBER: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

function parseNamedMonthDate(value: string): string {
  // RE&S consolidation uses "14 Oct 26"; others may use "14 Oct 2026".
  const match = value
    .trim()
    .match(/^(\d{1,2})[.\s/\-]+([A-Za-z]{3,9})[.\s/\-]+(\d{2,4})$/);
  if (!match) return '';
  const month = MONTH_NAME_TO_NUMBER[match[2]!.toLowerCase()] ?? 0;
  if (!month) return '';
  return parseDmyParts(Number(match[1]), month, Number(match[3]));
}

/**
 * Singapore / desktop DEM: slash-separated dates are always DD/MM/YYYY,
 * even when the PDF column order looks like US month/day.
 */
export function parseDeliveryDateDmy(raw: string): string {
  const value = raw.trim();
  if (!value) return '';

  const compact = value.replace(/\s+/g, '');

  const isoMatch = compact.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    if (isValidYmd(year, month, day)) return toIso(year, month, day);
    return '';
  }

  const ymdMatch = compact.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/);
  if (ymdMatch) {
    const year = Number(ymdMatch[1]);
    const month = Number(ymdMatch[2]);
    const day = Number(ymdMatch[3]);
    if (isValidYmd(year, month, day)) return toIso(year, month, day);
    return '';
  }

  const dmyMatch = value.match(/(\d{1,2})\s*[/.-]\s*(\d{1,2})\s*[/.-]\s*(\d{2,4})/);
  if (dmyMatch) {
    const parsed = parseDmyParts(Number(dmyMatch[1]), Number(dmyMatch[2]), Number(dmyMatch[3]));
    if (parsed) return parsed;
  }

  const named = parseNamedMonthDate(value);
  if (named) return named;

  return '';
}

export function formatDeliveryDateInput(raw: string): string {
  return parseDeliveryDateDmy(raw);
}

export function formatDeliveryDateDisplay(raw: string | null | undefined): string {
  if (!raw) return '—';
  const iso = parseDeliveryDateDmy(String(raw).split('T')[0] ?? '');
  if (!iso) return String(raw).trim() || '—';
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

export function parseDeliveryDateForDb(raw: string): string | null {
  const iso = parseDeliveryDateDmy(raw);
  return iso || null;
}

export function parseDeliveryDateToLocalDate(raw: string): Date {
  const iso = parseDeliveryDateDmy(raw);
  if (!iso) return new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year!, month! - 1, day);
}
